import { ref, type Ref } from 'vue';
import type { ReviewProjectInDb, ReviewProjectInDbDetail } from '@/api';
import { ReviewProjectService } from '@/services/reviewProjectService';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';

export interface UseReviewProject {
  project: Ref<ReviewProjectInDbDetail | null>;
  loading: Ref<boolean>;
  error: Ref<string | null>;
  fetchProject: () => Promise<void>;
  patchProject: (next: ReviewProjectInDb) => void;
}

/**
 * Loads the project every page of a review project needs before it can draw its
 * own breadcrumb and title — the workspace and each of its sub pages. One
 * function rather than a copy per host, so they cannot disagree about what a
 * failed load says or about which shape the project is held in.
 *
 * Not a store: this is a per-page fetch with a per-page error, and two pages
 * are never mounted at once. Cross-pane state belongs in `reviewProjectStore`.
 */
export function useReviewProject(projectUid: () => string): UseReviewProject {
  const project = ref<ReviewProjectInDbDetail | null>(null);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);

  async function fetchProject(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      project.value = await ReviewProjectService.getById(projectUid());
    } catch (error_) {
      error.value = extractErrorDetail(error_, 'Failed to load the review project.');
      logger.error('Failed to fetch review project', error_);
    } finally {
      loading.value = false;
    }
  }

  /**
   * A page that edits the project (settings) reports the saved record back, so
   * the breadcrumb and heading follow a rename. The data-room directory only
   * comes on the detail response, so it is kept from the fetched project.
   */
  function patchProject(next: ReviewProjectInDb): void {
    if (!project.value) return;
    project.value = { ...next, data_room_root_directory: project.value.data_room_root_directory };
  }

  return { project, loading, error, fetchProject, patchProject };
}
